import React, { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  strategyRdSectionCardClassName, strategyRdTooltipArrowClassName, strategyRdTooltipClassName } from "./strategyRdLabUi";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import {
  formatStrategyRdMoney,
  formatStrategyRdOutcomeCell,
  formatStrategyRdPrice,
  formatStrategyRdQty,
  formatStrategyRdRowExcursion,
  strategyRdExcursionUnitLabel,
  strategyRdMatchKey,
  strategyRdMoneyColumnLabel,
  strategyRdOutcomeNoteChips,
  strategyRdSkippedReasonLabel,
} from "../../../utils/strategyRd";
import { strategyRdPathInvalidReasonLabel } from "../../../utils/strategyRdPath";
import type {
  StrategyRdApplyOverview,
  StrategyRdApplyResult,
  StrategyRdBacktestWarning,
  StrategyRdExitLeg,
  StrategyRdFill,
  StrategyRdMarginSummary,
  StrategyRdMatch,
  StrategyRdParity,
} from "../../../types/strategyRd";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
} from "../Statistics/components/statsSignedPnlBarUtils";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import { StrategyRdBacktestWarnings } from "./StrategyRdBacktestWarnings";
import { StrategyRdStabilitySection } from "./StrategyRdStability";
import { StrategyRdSweepTableView } from "./coach/StrategyRdCoachLabActionCard";
import { StrategyRdOverviewStats, type StrategyRdReturnRange } from "./StrategyRdOverviewStats";
import { strategyRdMetaClassName } from "./strategyRdCardShell";

/** Match Trade Log desktop page size so a typical apply fits one page. */
const STRATEGY_RD_APPLY_PAGE_SIZE = 50;

export type StrategyRdApplyTableProps = {
  matches: StrategyRdMatch[];
  results: StrategyRdApplyResult[];
  overview: StrategyRdApplyOverview | null;
  selectedKey: string | null;
  timeZone: string;
  onSelect: (match: StrategyRdMatch) => void;
  /** When false, hide empty-state copy (strategy research-sim always has data). */
  showEmptyHint?: boolean;
  /** Account currency for money (USD unless the quote couldn't be converted). */
  currency?: string | null;
  margin?: StrategyRdMarginSummary | null;
  parity?: StrategyRdParity | null;
  /** BE caveats (§6); falls back to `overview.warnings`. */
  warnings?: StrategyRdBacktestWarning[] | null;
  onCheckRobustness?: () => void;
  onOpenSetting?: (field: string) => void;
  /** Robustness said "sensitive": the return range across feeds, shown by the return. */
  returnRange?: StrategyRdReturnRange | null;
  /** e.g. "Showing the first 100 trades" when the backtest's full trade list isn't stored. */
  tradesNote?: string | null;
  /** Lab results dock: stat cards are pinned above, so the Statistics tab skips them. */
  showHeroes?: boolean;
  /** Which tab opens first. */
  defaultTab?: "statistics" | "trades";
};

export type StrategyRdSideView = "combined" | "long" | "short";
type SideView = StrategyRdSideView;

/** The overview for one side (`by_side`), or the whole backtest. */
export function strategyRdSideOverview(
  overview: StrategyRdApplyOverview | null,
  side: StrategyRdSideView
): StrategyRdApplyOverview | null {
  const bySide = overview?.by_side ?? null;
  if (side === "long") return bySide?.long ?? null;
  if (side === "short") return bySide?.short ?? null;
  return overview;
}

export function strategyRdSideResults(
  results: StrategyRdApplyResult[],
  side: StrategyRdSideView
): StrategyRdApplyResult[] {
  return side === "combined" ? results : results.filter((r) => (r.fill?.direction || r.direction) === side);
}

export function strategyRdHasSideViews(overview: StrategyRdApplyOverview | null): boolean {
  return Boolean(overview?.by_side?.long || overview?.by_side?.short);
}

/** Combined / Longs / Shorts — hidden when the backtest has no per-side stats. */
export function StrategyRdSideToggle({
  overview,
  value,
  onChange,
}: {
  overview: StrategyRdApplyOverview | null;
  value: StrategyRdSideView;
  onChange: (side: StrategyRdSideView) => void;
}): JSX.Element | null {
  const bySide = overview?.by_side ?? null;
  if (!strategyRdHasSideViews(overview)) return null;
  return (
    <Tabs data-trade-log-tabs="" value={value} onValueChange={(v) => onChange(v as StrategyRdSideView)}>
      <TabsList className="min-w-max" data-strategy-rd-side-tabs="">
        <TabsTrigger value="combined">Combined</TabsTrigger>
        {bySide?.long ? <TabsTrigger value="long">Longs</TabsTrigger> : null}
        {bySide?.short ? <TabsTrigger value="short">Shorts</TabsTrigger> : null}
      </TabsList>
    </Tabs>
  );
}

type ApplyRow = {
  key: string;
  match: StrategyRdMatch;
  applied?: StrategyRdApplyResult;
};

function resultForMatch(
  match: StrategyRdMatch,
  byTime: Map<string, StrategyRdApplyResult>
): StrategyRdApplyResult | undefined {
  return byTime.get(match.bar_time) ?? byTime.get(strategyRdMatchKey(match));
}

function excursionTooltip(applied: StrategyRdApplyResult | undefined): string | null {
  const exc = applied?.excursion;
  if (!exc) return null;
  const mfeP = exc.mfe_price;
  const maeP = exc.mae_price;
  if (mfeP == null && maeP == null) return null;
  const unit = strategyRdExcursionUnitLabel(exc.unit);
  const lines = [
    unit ? `Unit: ${unit}` : null,
    mfeP != null ? `MFE price ${formatStrategyRdPrice(mfeP)}` : null,
    maeP != null ? `MAE price ${formatStrategyRdPrice(maeP)}` : null,
  ].filter(Boolean);
  return lines.length ? lines.join(" · ") : null;
}

function outcomeColor(result: string | null | undefined): string | undefined {
  if (result === "target") return PNL_POSITIVE_COLOR;
  if (result === "stop" || result === "liquidated") return PNL_NEGATIVE_COLOR;
  // Time-stop exits (and anything else) take their colour from the P&L column.
  return undefined;
}

/** The fill's result wins (liquidated / open / skipped by the account); else the path outcome. */
function rowResult(applied: StrategyRdApplyResult | undefined): string | null {
  return applied?.fill?.result || applied?.forward_outcome.result || null;
}

/** Actual exit: a gap fills at the bar's open, so never `levels.stop` / `levels.target`. */
function rowExitPrice(applied: StrategyRdApplyResult | undefined): number | null {
  return applied?.fill?.exit_price ?? applied?.forward_outcome.exit_price ?? null;
}

const LEG_KIND_LABELS: Record<string, string> = {
  partial: "partial",
  target: "target",
  stop: "stop",
  exit: "time stop",
};

/** "50 % @ 2 651.20 (partial)". */
export function strategyRdExitLegLabel(leg: StrategyRdExitLeg): string {
  const pct = Math.round(leg.frac * 1000) / 10;
  return `${pct} % @ ${formatStrategyRdPrice(leg.price)} (${LEG_KIND_LABELS[leg.kind] ?? leg.kind})`;
}

function ExitLegsRow({ legs }: { legs: StrategyRdExitLeg[] }): JSX.Element {
  return (
    <ul className="flex flex-col gap-0.5 py-1 pl-6 text-xs text-muted-foreground" data-strategy-rd-exit-legs="">
      {legs.map((leg, i) => (
        <li key={i}>
          {strategyRdExitLegLabel(leg)}
          {leg.bars != null ? ` · ${Math.round(leg.bars)}b` : ""}
        </li>
      ))}
    </ul>
  );
}

function marginCallsTooltip(
  fill: StrategyRdFill,
  timeZone: string,
  currency: string | null | undefined
): string {
  return (fill.margin_calls ?? [])
    .map((mc) =>
      [
        mc.bar_time ? formatTradeDateTimeForDisplay(mc.bar_time, timeZone) : null,
        mc.qty != null ? `qty ${formatStrategyRdQty(mc.qty)}` : null,
        mc.price != null ? `@ ${formatStrategyRdPrice(mc.price)}` : null,
        mc.pnl != null ? formatStrategyRdMoney(mc.pnl, currency) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    )
    .join("\n");
}

function hoverTip(text: React.ReactNode, tip: string, className?: string): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-no-row-click=""
          className={`cursor-help underline decoration-dotted underline-offset-2 ${className ?? ""}`}
        >
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent className={cn(strategyRdTooltipClassName, "whitespace-pre-line")} arrowClassName={strategyRdTooltipArrowClassName}>{tip}</TooltipContent>
    </Tooltip>
  );
}

function tradingViewChartUrl(tvSymbol: string): string {
  return `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tvSymbol)}`;
}

const PARITY_TOOLTIP =
  "Your backtest runs on our market data. On TradingView the same script trades the same way " +
  "when the data is the same exchange's (crypto, futures). For forex and gold every price feed " +
  "is slightly different. Including TradingView's. So some trades will differ; the " +
  "robustness test shows how much that can move your result.";

/** "Same data as TradingView" / "Approximate feed" — how far trades should match. */
export function StrategyRdParityBadge({
  parity,
}: {
  parity: StrategyRdParity | null | undefined;
}): JSX.Element | null {
  if (!parity) return null;
  const exact = parity.level === "exchange";
  const label = exact ? "Same data as TradingView" : "Approximate feed";
  const color = exact ? PNL_POSITIVE_COLOR : "#64748B";
  return (
    <div className="flex flex-wrap items-center gap-2" data-strategy-rd-parity={parity.level}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="cursor-help" style={{ color, borderColor: color }}>
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className={cn(strategyRdTooltipClassName, "max-w-xs")} arrowClassName={strategyRdTooltipArrowClassName}>{PARITY_TOOLTIP}</TooltipContent>
      </Tooltip>
      {parity.tv_symbol ? (
        <a
          href={tradingViewChartUrl(parity.tv_symbol)}
          target="_blank"
          rel="noreferrer"
          className={`${strategyRdMetaClassName} underline underline-offset-2 hover:text-foreground`}
        >
          Compare on TradingView
        </a>
      ) : null}
    </div>
  );
}

function pnlColor(value: number | null | undefined): string | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  if (value > 0) return PNL_POSITIVE_COLOR;
  if (value < 0) return PNL_NEGATIVE_COLOR;
  return undefined;
}

export type StrategyRdTradesCardProps = {
  matches: StrategyRdMatch[];
  results: StrategyRdApplyResult[];
  sideView: StrategyRdSideView;
  selectedKey: string | null;
  timeZone: string;
  currency?: string | null;
  tradesNote?: string | null;
  onSelect: (match: StrategyRdMatch) => void;
  /** Combined / Longs / Shorts in the card's header (needs the overview's per-side stats). */
  sideOverview?: StrategyRdApplyOverview | null;
  onSideChange?: (side: StrategyRdSideView) => void;
  /** The backtest's market: names rows for trades the chart's scan didn't find. */
  symbol?: string | null;
};

/** Every setup with its Entry / SL / TP, outcome and P&L — partial exits expand in place. */
export function StrategyRdTradesCard({
  matches,
  results,
  sideView,
  selectedKey,
  timeZone,
  currency = null,
  tradesNote = null,
  onSelect,
  sideOverview = null,
  onSideChange,
  symbol = null,
}: StrategyRdTradesCardProps): JSX.Element {
  const showCommission = useMemo(
    () => results.some((r) => r.fill?.commission != null && r.fill.commission !== 0),
    [results]
  );
  const byTime = useMemo(() => {
    const map = new Map<string, StrategyRdApplyResult>();
    for (const row of results) {
      map.set(row.bar_time, row);
      if (row.pattern_id) {
        map.set(`${row.pattern_id}:${row.bar_time}`, row);
      }
    }
    return map;
  }, [results]);

  const tableData: ApplyRow[] = useMemo(() => {
    const rows: ApplyRow[] = matches.map((match) => ({
      key: strategyRdMatchKey(match),
      match,
      applied: resultForMatch(match, byTime),
    }));
    // The backtest's own trades come first: a saved backtest opens before the chart's scan has
    // run (or on a window where it finds fewer setups), and its trades still belong here.
    const shown = new Set(rows.map((row) => row.applied).filter(Boolean));
    const missing = results.filter((r) => !shown.has(r));
    if (missing.length > 0) {
      const name = symbol || matches[0]?.symbol || "";
      for (const r of missing) {
        const match: StrategyRdMatch = {
          pattern_id: r.pattern_id || "backtest",
          symbol: name,
          candle_type: matches[0]?.candle_type || "",
          bar_time: r.bar_time,
          direction: r.direction,
          chart: r.chart,
        };
        rows.push({ key: strategyRdMatchKey(match), match, applied: r });
      }
      rows.sort((a, b) => Date.parse(a.match.bar_time) - Date.parse(b.match.bar_time));
    }
    return rows.filter(
      (row) =>
        sideView === "combined" ||
        (row.applied?.fill?.direction || row.applied?.direction || row.match.direction) === sideView
    );
  }, [byTime, matches, results, sideView, symbol]);

  /** One trade closed in parts at a time — its legs show in an expanded row. */
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: STRATEGY_RD_APPLY_PAGE_SIZE,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [results.length, matches.length, sideView]);

  const columns: ColumnDef<ApplyRow, unknown>[] = useMemo(
    () => [
      {
        id: "symbol",
        accessorFn: (row) => row.match.symbol,
        meta: { label: "Symbol" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Symbol" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium">
            {row.original.match.symbol}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "type",
        accessorFn: (row) => row.match.direction,
        meta: { label: "Type" },
        header: "Type",
        enableSorting: false,
        cell: ({ row }) => {
          const d = row.original.match.direction;
          if (d === "short") return <TradePositionSideBadge side="short" />;
          if (d === "neutral") {
            return <TradeLogSortableCell className="font-medium">Neutral</TradeLogSortableCell>;
          }
          return <TradePositionSideBadge side="long" />;
        },
      },
      {
        id: "entryDate",
        accessorFn: (row) => row.match.bar_time,
        meta: { label: "Entry Date" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Entry Date" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell>
            {formatTradeDateTimeForDisplay(row.original.match.bar_time, timeZone)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "entryPrice",
        accessorFn: (row) => row.applied?.levels.entry ?? null,
        meta: { label: "Entry Price" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Entry Price" />
        ),
        cell: ({ row }) => {
          const levels = row.original.applied?.levels;
          const valid = levels?.valid !== false;
          return (
            <TradeLogSortableCell className="font-medium">
              {valid ? formatStrategyRdPrice(levels?.entry) : "N/A"}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "stop",
        accessorFn: (row) => row.applied?.levels.stop ?? null,
        meta: { label: "Stop" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Stop" />
        ),
        cell: ({ row }) => {
          const levels = row.original.applied?.levels;
          const valid = levels?.valid !== false;
          return (
            <TradeLogSortableCell className="font-medium">
              {valid ? formatStrategyRdPrice(levels?.stop) : "N/A"}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "target",
        accessorFn: (row) => row.applied?.levels.target ?? null,
        meta: { label: "Target" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Target" />
        ),
        cell: ({ row }) => {
          const levels = row.original.applied?.levels;
          const valid = levels?.valid !== false;
          return (
            <TradeLogSortableCell className="font-medium">
              {valid ? formatStrategyRdPrice(levels?.target) : "N/A"}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "exitPrice",
        accessorFn: (row) => rowExitPrice(row.applied),
        meta: { label: "Exit Price" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Exit Price" />
        ),
        cell: ({ row }) => {
          const applied = row.original.applied;
          const exit = rowExitPrice(applied);
          const result = rowResult(applied);
          const text =
            exit != null && result !== "open" && result !== "skipped"
              ? formatStrategyRdPrice(exit)
              : "N/A";
          const legs = applied?.forward_outcome.legs ?? [];
          if (legs.length > 1 && exit != null) {
            const open = expandedKey === row.original.key;
            return (
              <TradeLogSortableCell className="font-medium">
                <button
                  type="button"
                  data-no-row-click=""
                  className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
                  aria-expanded={open}
                  title="Average of the pieces: show each"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedKey(open ? null : row.original.key);
                  }}
                >
                  {open ? (
                    <ChevronDown className="size-3.5" aria-hidden />
                  ) : (
                    <ChevronRight className="size-3.5" aria-hidden />
                  )}
                  {text}
                  <span className="font-normal text-muted-foreground">avg · {legs.length} parts</span>
                </button>
              </TradeLogSortableCell>
            );
          }
          return (
            <TradeLogSortableCell className="font-medium">
              {(applied?.forward_outcome.note ?? "").split("|").includes("gap_open") && exit != null
                ? hoverTip(text, "Gapped past the level, filled at the bar's open, as on TradingView.")
                : text}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "outcome",
        accessorFn: (row) => rowResult(row.applied) ?? "",
        meta: { label: "Outcome" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Outcome" />
        ),
        cell: ({ row }) => {
          const applied = row.original.applied;
          const fo = applied?.forward_outcome;
          const fill = applied?.fill;
          const result = rowResult(applied);
          const color = outcomeColor(result);
          const label = formatStrategyRdOutcomeCell({
            result,
            barsToEvent: fill?.bars_to_event ?? fo?.bars_to_event,
          });
          const skipped =
            result === "skipped"
              ? strategyRdSkippedReasonLabel(fill?.skipped_reason ?? fo?.note)
              : null;
          const calls = fill?.margin_calls ?? [];
          const chips = result === "skipped" ? [] : strategyRdOutcomeNoteChips(fo?.note);
          const invalid = applied?.levels.valid === false ? applied.levels.invalid_reason : null;
          return (
            <TradeLogSortableCell className="font-medium">
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <span data-strategy-rd-outcome="" style={color ? { color } : undefined}>
                  {invalid
                    ? hoverTip("No trade", strategyRdPathInvalidReasonLabel(invalid))
                    : label}
                  {skipped ? (
                    <span className="font-normal text-muted-foreground"> · {skipped}</span>
                  ) : null}
                </span>
                {chips.map((chip) => (
                  <Tooltip key={chip.key}>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        data-no-row-click=""
                        data-strategy-rd-note-chip={chip.key}
                        className="h-5 cursor-help px-1.5 text-[10px] font-normal"
                      >
                        {chip.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className={strategyRdTooltipClassName} arrowClassName={strategyRdTooltipArrowClassName}>{chip.tip}</TooltipContent>
                  </Tooltip>
                ))}
                {calls.length > 0 && fill
                  ? hoverTip(
                      <AlertTriangle
                        className="size-3.5 text-muted-foreground"
                        aria-label="Margin call"
                        data-strategy-rd-margin-call=""
                      />,
                      `TradingView would margin-call this trade:\n${marginCallsTooltip(fill, timeZone, currency)}`,
                      "no-underline"
                    )
                  : null}
              </span>
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "qty",
        accessorFn: (row) => row.applied?.fill?.qty ?? null,
        meta: { label: "Qty" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Qty" />,
        cell: ({ row }) => {
          const fill = row.original.applied?.fill;
          const text = formatStrategyRdQty(fill?.qty);
          return (
            <TradeLogSortableCell className="font-medium">
              {fill?.size_capped
                ? hoverTip(
                    text,
                    `Cut to fit margin (wanted ${formatStrategyRdQty(fill.size_wanted)})`,
                    "text-[color:var(--brand-tropical-surf)]"
                  )
                : text}
            </TradeLogSortableCell>
          );
        },
      },
      ...(showCommission
        ? [
            {
              id: "commission",
              accessorFn: (row: ApplyRow) => row.applied?.fill?.commission ?? null,
              meta: { label: "Commission" },
              header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Commission" />
              ),
              cell: ({ row }) => (
                <TradeLogSortableCell>
                  {formatStrategyRdMoney(row.original.applied?.fill?.commission, currency) ?? "N/A"}
                </TradeLogSortableCell>
              ),
            } as ColumnDef<ApplyRow, unknown>,
          ]
        : []),
      {
        id: "pnl",
        accessorFn: (row) => row.applied?.fill?.pnl ?? null,
        meta: { label: strategyRdMoneyColumnLabel("PnL", currency) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={strategyRdMoneyColumnLabel("PnL", currency)} />
        ),
        cell: ({ row }) => {
          const pnl = row.original.applied?.fill?.pnl;
          const text = formatStrategyRdMoney(pnl, currency);
          const color = pnlColor(pnl);
          return (
            <TradeLogSortableCell className="font-medium">
              <span style={color ? { color } : undefined}>{text ?? "N/A"}</span>
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "excursion",
        accessorFn: (row) => row.applied?.excursion?.mfe ?? null,
        meta: { label: "Excursion" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Excursion" />
        ),
        cell: ({ row }) => {
          const applied = row.original.applied;
          const excursionText = formatStrategyRdRowExcursion(applied?.excursion);
          const tip = excursionTooltip(applied);
          if (!excursionText) {
            return <TradeLogSortableCell>N/A</TradeLogSortableCell>;
          }
          if (!tip) {
            return <TradeLogSortableCell>{excursionText}</TradeLogSortableCell>;
          }
          return (
            <TradeLogSortableCell>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-no-row-click=""
                    className="cursor-help underline decoration-dotted underline-offset-2"
                  >
                    {excursionText}
                  </span>
                </TooltipTrigger>
                <TooltipContent className={strategyRdTooltipClassName} arrowClassName={strategyRdTooltipArrowClassName}>{tip}</TooltipContent>
              </Tooltip>
            </TradeLogSortableCell>
          );
        },
      },
    ],
    [currency, expandedKey, showCommission, timeZone]
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-statistics-shadcn-card=""
        className={strategyRdSectionCardClassName}
      >
        {/* No "Trades" title: the tab already names it. Just the note and the side toggle. */}
        {tradesNote || (onSideChange && strategyRdHasSideViews(sideOverview)) ? (
          <div className="flex flex-wrap items-center gap-2">
            {onSideChange ? (
              <StrategyRdSideToggle overview={sideOverview} value={sideView} onChange={onSideChange} />
            ) : null}
            {tradesNote ? (
              <span className={strategyRdMetaClassName} data-strategy-rd-trades-note="">
                {tradesNote}
              </span>
            ) : null}
          </div>
        ) : null}
        <DataTable
          columns={columns}
          data={tableData}
          getRowId={(row) => row.key}
          activeRowId={selectedKey ?? undefined}
          onRowClick={(row) => onSelect(row.match)}
          pagination={pagination}
          onPaginationChange={setPagination}
          expandedRowId={expandedKey ?? undefined}
          renderExpandedRow={(row) => (
            <ExitLegsRow legs={row.applied?.forward_outcome.legs ?? []} />
          )}
          showPagination
          tableClassName="dark:!bg-sidebar"
          emptyMessage="No apply results."
        />
      </div>
    </TooltipProvider>
  );
}

export function StrategyRdApplyTable({
  matches,
  results,
  overview,
  selectedKey,
  timeZone,
  onSelect,
  showEmptyHint = true,
  currency = null,
  margin = null,
  parity = null,
  warnings = null,
  onCheckRobustness,
  onOpenSetting,
  returnRange = null,
  tradesNote = null,
  showHeroes = true,
  defaultTab = "statistics",
}: StrategyRdApplyTableProps): JSX.Element | null {
  const shownWarnings = warnings ?? overview?.warnings ?? null;
  const [sideView, setSideView] = useState<SideView>("combined");
  const hasSideTabs = strategyRdHasSideViews(overview);
  useEffect(() => {
    if (!hasSideTabs) setSideView("combined");
  }, [hasSideTabs]);
  const sideOverview = strategyRdSideOverview(overview, sideView);
  const sideResults = useMemo(() => strategyRdSideResults(results, sideView), [results, sideView]);
  /** Each result's fill (entry / exit times) — "Time to exit" in Outcomes. */
  const resultFills = useMemo(() => sideResults.map((row) => row.fill ?? null), [sideResults]);

  if (results.length === 0 && showEmptyHint) {
    return (
      <p className={strategyRdMetaClassName}>
        Apply the template to fill Entry / SL / TP and a forward outcome (target / stop / open).
      </p>
    );
  }

  if (results.length === 0) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-0 flex-col gap-4" data-strategy-rd-apply-table="">
        {parity ? <StrategyRdParityBadge parity={parity} /> : null}
        <StrategyRdBacktestWarnings
          warnings={shownWarnings}
          onCheckRobustness={onCheckRobustness}
          onOpenSetting={onOpenSetting}
        />
        {/* Older backtests without `warnings[]`: the margin-call banner of §2. */}
        {shownWarnings == null && margin?.margin_calls != null && margin.margin_calls > 0 ? (
          <Alert data-strategy-rd-margin-banner="">
            <AlertTriangle className="size-4 text-muted-foreground" aria-hidden />
            <AlertDescription className={strategyRdMetaClassName}>
              TradingView would have margin-called {margin.margin_calls} trade
              {margin.margin_calls === 1 ? "" : "s"}. Lower the risk % or raise leverage.
            </AlertDescription>
          </Alert>
        ) : null}
        <StrategyRdStabilitySection
          split={(sideOverview ?? overview)?.split}
          stability={overview?.stability}
        />
        {overview?.sweep ? (
          <div className="flex flex-col gap-1">
            <p className={strategyRdMetaClassName}>
              Parameter search: the best row (on the tuning period) is this backtest.
            </p>
            <StrategyRdSweepTableView sweep={overview.sweep} />
          </div>
        ) : null}
        <StrategyRdSideToggle overview={overview} value={sideView} onChange={setSideView} />
        <Tabs data-trade-log-tabs="" defaultValue={defaultTab} className="w-full min-w-0">
          <TabsList className="min-w-max">
            <TabsTrigger value="statistics">Statistics</TabsTrigger>
            <TabsTrigger value="trades">Trades</TabsTrigger>
          </TabsList>

          <TabsContent value="statistics" className="mt-4 flex flex-col gap-4">
            <StrategyRdOverviewStats
              overview={sideOverview}
              showHeroes={showHeroes}
              fills={resultFills}
              currency={currency}
              margin={sideView === "combined" ? margin : null}
              returnRange={sideView === "combined" ? returnRange : null}
            />
          </TabsContent>

          <TabsContent value="trades" className="mt-4">
            <StrategyRdTradesCard
              matches={matches}
              results={results}
              sideView={sideView}
              selectedKey={selectedKey}
              timeZone={timeZone}
              currency={currency}
              tradesNote={tradesNote}
              onSelect={onSelect}
            />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}
